import React, { useEffect, useState, Dispatch, SetStateAction } from 'react'
import { FilterHeader, FilterHeaderWithIcon } from 'src/lib/types/genericTable'
import { Menu, MenuItem, Checkbox, ListItemText, Button } from '@mui/material'

interface TableFilterHeaderProps {
  loading: boolean
  filterHeader: FilterHeader[]
  setFilterHeaders: Dispatch<SetStateAction<FilterHeader[]>>
  filterHeaderWithIcon: FilterHeaderWithIcon[]
  setFilterHeaderWithIcon: Dispatch<SetStateAction<FilterHeaderWithIcon[]>>
  handleStatusSection?: (label: string, section: FilterHeader[]) => FilterHeader[]
  handleFilterClick?: (label: string, state: boolean) => void
  isDisabled?: boolean
}

const TableFilterHeader: React.FC<TableFilterHeaderProps> = ({
  loading,
  filterHeader,
  setFilterHeaders,
  filterHeaderWithIcon,
  setFilterHeaderWithIcon,
  handleStatusSection,
  handleFilterClick,
  isDisabled
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const selectedFilters = filterHeaderWithIcon[0]?.options
    .filter(option => option.isActive)
    .map(option => ({ id: option.id, count: option.count }))

  // Open/close menu
  const handleMenuToggle = (event: React.MouseEvent<HTMLElement> | null) => {
    setAnchorEl(event ? event.currentTarget : null)
  }

  const handleClearFilter = () => {
    selectedFilters.forEach(({ id }) => handleFilterClick?.(id, false))
    setAnchorEl(null)
  }

  const selectedPillCount = selectedFilters.reduce((acc, curr) => acc + (curr.count || 0), 0)

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    handleFilterClick?.(id, event.target.checked)
  }

  const handleStatusClick = (label: string) => {
    //If currently active filter is clicked then do nothing
    if (filterHeader.find(header => header.label === label && header.isActive)) return
    if (handleStatusSection) {
      setFilterHeaderWithIcon(prev =>
        prev.map(header => ({
          ...header,
          options: header.options.map(option => {
            return {
              ...option,
              isActive: false
            }
          })
        }))
      )
      handleStatusSection(label, filterHeader)
    }
  }

  // Render filter header
  const renderFilterHeader = () =>
    filterHeader.map(({ label, isActive, count }) => (
      <div
        key={label}
        className={`flex items-center gap-2 py-1 px-2 cursor-pointer ${
          isActive
            ? 'bg-[var(--flo-gray-light-color)] text-[var(--flo-coal-light-color)] border rounded-lg font-medium'
            : 'text-[var(--flo-gray-dark-color)]'
        }`}
        onClick={() => handleStatusClick(label)}
      >
        <div>{label}</div>
        {count !== undefined && (
          <div className='px-2 py-1 h-5 w-6 flex justify-center items-center bg-gray-200 rounded-lg text-xs'>
            {loading || count < 0 ? '' : isActive && selectedFilters.length > 0 ? selectedPillCount : count}
          </div>
        )}
      </div>
    ))

  // Render filter header with icon
  const renderFilterHeaderWithIcon = () =>
    filterHeaderWithIcon.map(({ label, icon: Icon, options }, index) => (
      <div key={index} className='p-2 font-semibold flex items-center hover:bg-none'>
        <div onClick={e => handleMenuToggle(e)} className='flex items-center cursor-pointer'>
          {Icon && <Icon />}
          {label}
        </div>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleMenuToggle(null)}>
          <ListItemText primary={''} />

          {options.map(({ id, label: filterLabel, count }) => (
            <MenuItem
              key={id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                paddingY: '0px',
                '&:hover': { backgroundColor: 'white' }
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', width: '100%' }}>
                <Checkbox
                  checked={selectedFilters.some(filter => filter.id === id)}
                  onChange={e => handleCheckboxChange(e, id)}
                  color='primary'
                />
                <ListItemText
                  primary={filterLabel}
                  sx={{
                    flex: '0 1 auto',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                />
                {count !== undefined && count >= 0 && (
                  <span className='ml-2 px-2 py-1 bg-gray-200 rounded-lg text-xs text-center font text-coal-light'>
                    {count}
                  </span>
                )}
              </label>
            </MenuItem>
          ))}
          <Button
            sx={{
              padding: '0px 0px 10px 30px',
              color: 'var(--flo-gray-dark-color)',
              '&:hover': { backgroundColor: 'transparent' }
            }}
            onClick={handleClearFilter}
          >
            <ListItemText
              primaryTypographyProps={{
                style: { color: selectedFilters.length ? 'var(--flo-coal-dark-color' : 'var(  --flo-gray-dark-color)' }
              }}
              primary='Clear filter'
            />
          </Button>
        </Menu>
      </div>
    ))

  return (
    <div className='flex items-center justify-center rounded-t-lg border-b p-2 px-3 bg-white h-14'>
      <div className='flex gap-2 mr-auto'>{renderFilterHeader()}</div>
      <div className='flex items-center justify-center'>
        {renderFilterHeaderWithIcon()}
        {selectedFilters.length > 0 && (
          <div className='relative flex items-center justify-center'>
            <span className='inline-flex h-2 w-2 rounded-full bg-primary-dark'></span>
          </div>
        )}
      </div>
    </div>
  )
}

export default TableFilterHeader
